import { useEffect, useMemo, useState } from 'react';
import { NLError, NLRequest, NLResponse } from '../api/ask.types';
import { ask } from '../api/ask';
import { Status } from '../domain/status';
import { RetryConfig } from '../models';

export type NaturalLanguageRequest = Omit<NLRequest, 'threadId'> & RetryConfig;

interface NLWebQueryState {
    request: NaturalLanguageRequest;
    response: NLResponse | NLError | null;
    status: Status;
    threadId: string;
    error: string | null;
}

/**
 * Hook to fetch results from for Natural Language Search
 *
 * @param request
 * @returns
 */
export const useAsk = (pendingRequest: NaturalLanguageRequest) => {
    const [threadId, setThreadId] = useState('');
    const [state, setState] = useState<NLWebQueryState>({
        request: pendingRequest,
        response: null,
        status: Status.Initial,
        threadId: threadId,
        error: null,
    });

    useEffect(() => {
        if (typeof window !== 'undefined' && window.crypto.randomUUID) {
            setThreadId(window.crypto.randomUUID());
        } else {
            setThreadId(Math.random().toString(36).substring(2, 15));
        }
    }, []);

    const { request, response, status, error } = state;

    /**
     * If the request has changed, reset the state
     */
    useEffect(() => {
        const allKeys = [...new Set([...Object.keys(request), ...Object.keys(pendingRequest)])];
        const diff = allKeys.filter((key) => {
            const valueSame = request[key] === pendingRequest[key];
            if (!valueSame && Array.isArray(request[key]) && Array.isArray(pendingRequest[key])) {
                return request[key].length !== pendingRequest[key].length;
            }
            return !valueSame;
        });

        if (diff.length === 0) {
            return;
        }

        setState((prevState) => ({
            ...prevState,
            request: pendingRequest,
            response: null,
            status: Status.Initial,
            error: null,
        }));
    }, [pendingRequest, setState, request]);

    /**
     * If the status is idle, fetch the results
     */
    useEffect(() => {
        if (status !== Status.Initial) {
            return;
        }

        if (!request.query) {
            return;
        }

        const fetchResults = async () => {
            setState((prevState) => ({
                ...prevState,
                status: Status.Loading,
            }));

            const res = await ask({ ...request, threadId });

            if ('error' in res) {
                console.error(
                    `Error fetching Natural Language results. ${res.message}.`,
                    res.error,
                );
                setState((prevState) => ({
                    ...prevState,
                    status: Status.Error,
                    error: res.message,
                }));

                return;
            }

            setState((prevState) => ({
                ...prevState,
                request,
                response: res,
                status: Status.Ready,
                error: null,
            }));
        };

        fetchResults();
    }, [request, status, threadId, setState]);

    const result = useMemo(
        () => ({
            data: response,
            status,
            error,
        }),
        [response, status, error],
    );

    return result;
};
